import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for auto-saving with debounce functionality.
 * @param {string} initialValue The initial value for the textarea.
 * @param {function} saveFunction The function that saves the data (e.g., API call).
 * @param {number} debounceDelay The delay in milliseconds before the save function is called.
 * @returns {[string, boolean, function]} Returns the current value, the saving state, and the change handler.
 */
const useAutoSave = (initialValue, saveFunction, debounceDelay = 30000) => {
    const [value, setValue] = useState(initialValue);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedValue, setLastSavedValue] = useState(initialValue); // Track last saved value

    // Use ref to avoid re-creating the save function unnecessarily
    const saveFunctionRef = useRef(saveFunction);

    useEffect(() => {
        saveFunctionRef.current = saveFunction;
    }, [saveFunction]);

    const saveData = useCallback(async (val) => {
        setIsSaving(true);
        try {
            await saveFunctionRef.current(val);
            setLastSavedValue(val); // Update the last saved value
        } finally {
            setIsSaving(false);
        }
    }, []);

    useEffect(() => {
        // Avoid running on initial render or when `value` hasn't changed
        if (value === lastSavedValue) return;

        const handler = setTimeout(() => {
            saveData(value);
        }, debounceDelay);

        return () => clearTimeout(handler);
    }, [value, debounceDelay, saveData, lastSavedValue]);

    return [value, isSaving, setValue];
};

export default useAutoSave;
